import React, { useState } from 'react'

import FormControlLabel from '@material-ui/core/FormControlLabel'
import type { Theme, WithStyles } from '@material-ui/core/styles'
import { createStyles, withStyles } from '@material-ui/core/styles'
import WarningIcon from '@material-ui/icons/Warning'
import { Octokit } from '@octokit/core'
import {
  getDetails,
  getFeature,
  getObjectLabels,
  getQuoteOptions,
  hasIntegrationInterface,
  setDetails,
  setQuoteOptions
} from '@paintscout/util/builder'
import { getDebugInfo } from '@paintscout/util/builder/quote/debug/get-debug-info'
import type { WithClientOptions } from '@paintscout/ui'
import {
  Alert,
  Button,
  Checkbox,
  ConfirmationCheckbox,
  DropdownSelect,
  EditorDialog,
  FormSection,
  FormSectionTitle,
  Grid,
  HtmlContent,
  InputField,
  Tooltip,
  Typography,
  useDialogs,
  useUser,
  withClientOptions
} from '@paintscout/ui'
import type { WithQuoteContext } from '@paintscout/react-quote'
import {
  EditableToggle,
  withQuoteContext,
  GithubIssueDialog,
  LinkGithubIssueDialog,
  TileListDialog
} from '@paintscout/react-quote'
import { useSnackbar } from 'notistack'
import CopyToClipboard from 'react-copy-to-clipboard'

import find from 'lodash/find'
import get from 'lodash/get'

import { calculate } from '@paintscout/util/calculator'
import { CURRENT_QUOTE_VERSION } from '@paintscout/util/constants'
import { isEmpty, isQuoteEditable } from '@paintscout/util/util'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(0, 2)
    },
    formSection: {
      padding: theme.spacing(2, 0)
    },
    clientNote: {
      margin: 0
    },

    displayOptionsSection: {
      paddingTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column'
    },

    checkboxLabel: {
      ...theme.typography.body1
    },

    checkboxItemWrapper: {
      marginBottom: theme.spacing()
    },
    debugInfoTitle: {
      display: 'flex',
      alignItems: 'start'
    },
    warningIcon: {
      color: theme.palette.warning.main,
      fontSize: theme.typography.pxToRem(20),
      marginLeft: theme.spacing(4)
    }
  })

export interface QuoteAdvancedFormProps extends WithStyles<typeof styles>, WithQuoteContext, WithClientOptions {
  editable?: boolean
  onToggleEdit?: (editStatus: boolean) => void
  permissionToEdit?: boolean
  onClick?: (event: React.MouseEvent) => any
}

function QuoteAdvancedForm({
  onToggleEdit,
  permissionToEdit,
  quoteContext,
  clientOptions,
  classes,
  onClick,
  editable
}: QuoteAdvancedFormProps) {
  const { quote } = quoteContext
  const { options, save: saveOptions } = clientOptions

  const octokit = new Octokit({
    auth: process.env.GH_PAT
  })

  const quoteOptions = getQuoteOptions({ quote, options })
  const paginate = quote?.paginateWorkOrder ?? 0
  const details = getDetails({ quote, options })

  const { user, isSuperadmin, isSupport } = useUser()
  const clientNote = details?.notes?.client ?? '<p></p>'

  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()

  const advancedOptionsFeature = getFeature({ options, path: 'quotes.sidebar.advanced' })
  const sourceFeature = getFeature({ options, path: 'quotes.sidebar.advanced.source' })
  const showDebugInfo = !!isSuperadmin || !!isSupport
  const debugInfo = JSON.stringify(getDebugInfo({ user, quote }), null, 2)

  const hasClientNote = !isEmpty(clientNote)

  const objectLabels = getObjectLabels({ options })

  const { enqueueSnackbar } = useSnackbar()

  const { preferences, trialExpired } = useUser()

  const [editToggleAnchor, setEditToggleAnchor] = useState<{
    top: number
    left: number
  }>(null)
  const [isEditing, setIsEditing] = useState(editable)
  const isEditable = permissionToEdit && (isQuoteEditable(quote) || isEditing) && !trialExpired

  function handleCalculationLock(ev: any, value: boolean) {
    quoteContext.updateQuote({
      quote: {
        ...quote,
        calculationLock: value
      }
    })
  }

  function handlePaginateChange(ev: any) {
    quoteContext.updateQuote({
      quote: {
        ...quote,
        paginateWorkOrder: ev?.target?.value ?? 0
      }
    })
  }

  function handleVersionUpdate() {
    quoteContext.updateQuote({
      quote: calculate(
        {
          ...quote,
          version: CURRENT_QUOTE_VERSION
        },
        options
      )
    })
  }

  function handleOptionChange(ev: any, value: boolean) {
    quoteContext.updateQuote({
      quote: setQuoteOptions({
        quote,
        quoteOptions: {
          ...quoteOptions,
          [ev.currentTarget.name]: value
        }
      })
    })
  }

  function handleCheckboxChange(ev: React.ChangeEvent<HTMLInputElement>, value: boolean) {
    const { name } = ev.target

    quoteContext.updateQuote({
      quote: setDetails({
        quote,
        options,
        details: {
          ...details,
          [name]: value
        }
      })
    })
  }

  function handleClientNoteChange(value: string) {
    quoteContext.updateQuote({
      quote: setDetails({
        quote,
        options,
        details: {
          ...details,
          notes: {
            ...details.notes,
            client: value
          }
        }
      })
    })
  }

  function handleSourceChange(option) {
    quoteContext.updateQuote({
      quote: setDetails({
        quote,
        options,
        details: {
          ...details,
          source: { ...details.source, provider: option.value }
        }
      })
    })
  }

  function handleSourceIdChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const { value } = ev.target
    quoteContext.updateQuote({
      quote: setDetails({
        quote,
        options,
        details: {
          ...details,
          source: { ...details.source, id: value }
        }
      })
    })
  }
  function handleAccountIdChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const { value } = ev.target
    quoteContext.updateQuote({
      quote: setDetails({
        quote,
        options,
        details: {
          ...details,
          source: { ...details.source, accountId: value }
        }
      })
    })
  }

  function handleIssueReport() {
    openDialog(TileListDialog, {
      title: 'Report Issue',
      items: [
        {
          key: 'create',
          label: 'Create New Issue'
        },
        {
          key: 'link',
          label: 'Link Existing Issue'
        }
      ],
      onItemSelect: (key) => {
        if (key === 'create') {
          openDialog(GithubIssueDialog, {
            octokit,
            debugInfo,
            onConfirm: async (issue) => {
              await saveOptions({
                options: {
                  ...options,
                  options: {
                    ...options.options,
                    linkedIssues: [
                      ...(options.options.linkedIssues || []).filter((linkedIssue) => linkedIssue !== issue.number),
                      issue.number
                    ]
                  }
                }
              })
              dismissAllDialogs()
            }
          })
        } else if (key === 'link') {
          openDialog(LinkGithubIssueDialog, {
            octokit,
            debugInfo,
            onConfirm: async (issue) => {
              await saveOptions({
                options: {
                  ...options,
                  options: {
                    ...options.options,
                    linkedIssues: [
                      ...(options.options.linkedIssues || []).filter((linkedIssue) => linkedIssue !== issue.number),
                      issue.number
                    ]
                  }
                }
              })
              dismissAllDialogs()
            }
          })
        }
      }
    })
  }

  function renderQuoteOptions(advancedOptionsFeature: any) {
    const fields = [
      ['showQuantities', 'Show Quantity Value for Quantity-Based Rates'],
      [
        'groupHours',
        `Show Sub-Total Groups for ${objectLabels.quote.plural.toLowerCase()} with Multiple ${
          objectLabels.quote.value
        } Types`
      ],
      ['separateThousands', 'Separate Thousands Values with commas'],
      ['showUnits', 'Show currency & hour units beside values'],
      ['showDetailedBreakdown', `Show each area ${objectLabels.substrate.value} as an additional row with price`],
      ['alwaysShowJobAddress', 'Always show job address'],
      ['showWorkOrderClientNotes', 'Show Client notes on Work Order'],
      ['hideWorkOrderVolumes', 'Hide Product Volumes on Work Order'],
      ['stackSubstrates', `Show each area ${objectLabels.substrate.value} as an additional row without price`],
      ['hideWorkOrderMaterialCost', 'Hide Material Costs on Work Order']
    ]

    const enabledFields = fields.filter((field) => get(advancedOptionsFeature, `displayOptions.${field[0]}`, false))

    if (enabledFields.length === 0) {
      return null
    }

    return (
      <FormSection
        onClick={(ev: React.MouseEvent) => {
          // only call ev.stopPropagation when we're not editable
          if (!isEditable) {
            ev.stopPropagation()
            setEditToggleAnchor({
              top: ev.clientY,
              left: ev.clientX
            })
          }
        }}
      >
        <FormSectionTitle
          title={'Display Options'}
          variant={'h4'}
          subTitle={`How ${objectLabels.quote.plural} look to you, your crew, and customers.`}
        />

        {enabledFields.map((field, index) => {
          const checked = get(quoteOptions, field[0], false)

          const control = (
            <Checkbox checked={checked} onChange={handleOptionChange} name={field[0]} disabled={!isEditable} />
          )
          return (
            <div key={index} className={classes.checkboxItemWrapper}>
              <FormControlLabel
                key={field[0]}
                control={control}
                label={field[1]}
                classes={{ label: classes.checkboxLabel }}
              />
            </div>
          )
        })}
      </FormSection>
    )
  }

  function handleEditClientNote() {
    if (!editable) {
      return
    }
    openDialog(EditorDialog, {
      title: 'Edit Client Note',
      inputProps: { label: 'Client Note', fullWidth: true },
      value: clientNote,
      onConfirm: (ev: any, val: string) => {
        handleClientNoteChange(val)
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function renderAdvancedFields() {
    const sourceOptions = [
      hasIntegrationInterface({ preferences, options, integration: 'workglue' })
        ? { label: 'Workglue', value: 'workglue' }
        : null,
      hasIntegrationInterface({ preferences, options, integration: 'pipeline_deals' })
        ? { label: 'Pipeline CRM', value: 'pipeline_deals' }
        : null,
      hasIntegrationInterface({ preferences, options, integration: 'salesforce' })
        ? { label: 'SalesForce', value: 'salesforce' }
        : null,
      hasIntegrationInterface({ preferences, options, integration: 'salesforcesandbox' })
        ? { label: 'SalesForce (Sandbox)', value: 'salesforcesandbox' }
        : null
    ].filter((item) => item)
    const sourceProviderOption = find(sourceOptions, { value: details.source?.provider })

    const serviceTitanSource = (quote.sources ?? {})?.serviceTitan
    // const salesforceSource = (quote.sources ?? {})?.salesforce

    function toggleEditable(ev: React.MouseEvent) {
      // only call ev.stopPropagation when we're not editable
      if (!isEditable) {
        ev.stopPropagation()
        setEditToggleAnchor({
          top: ev.clientY,
          left: ev.clientX
        })
      }
    }

    return (
      <>
        <FormSection hideDivider onClick={toggleEditable} classes={{ root: classes.formSection }}>
          <FormSectionTitle
            title={'Client Note'}
            variant={'h4'}
            subTitle={`Display a note or Scope of Work directly below your ${objectLabels.quote.value}`}
          />
          <Grid container spacing={2}>
            {hasClientNote && (
              <Grid item xs={12}>
                <div onClick={handleEditClientNote}>
                  <Alert icon={null} noMargin>
                    <HtmlContent content={clientNote} />
                  </Alert>
                </div>
              </Grid>
            )}
            {!hasClientNote && (
              <Grid item xs={12}>
                <Button variant="text" onClick={handleEditClientNote} disabled={!isEditable}>
                  Add Client Note
                </Button>
              </Grid>
            )}
          </Grid>
        </FormSection>

        <FormSection onClick={toggleEditable} classes={{ root: classes.formSection }}>
          <FormSectionTitle
            title={`${objectLabels.quote.value} Visibility`}
            variant={'h4'}
            subTitle={`Revoke access to ${objectLabels.quote.plural.toLowerCase()} that have already been sent`}
          />
          <Grid container>
            <Grid item xs={12}>
              <FormControlLabel
                label={'Client Visible'}
                control={
                  <ConfirmationCheckbox
                    checked={details.clientVisible}
                    name={'clientVisible'}
                    dialogTitle={'Are you sure?'}
                    dialogMessage={`Customers will not be able to view this ${objectLabels.quote.value}.`}
                    direction={'dangerousToUncheck'}
                    customOnChange={handleCheckboxChange}
                    disabled={!isEditable}
                  />
                }
              />
              <FormControlLabel
                label={'Crew Visible'}
                control={
                  <Checkbox
                    checked={details.crewVisible}
                    name={'crewVisible'}
                    onChange={handleCheckboxChange}
                    disabled={!isEditable}
                  />
                }
              />
            </Grid>
          </Grid>
        </FormSection>

        {sourceFeature && (
          <FormSection onClick={toggleEditable} classes={{ root: classes.formSection }}>
            <FormSectionTitle
              title={`Integrations`}
              variant={'h4'}
              subTitle={`If this ${objectLabels.quote.value.toLowerCase()} was created in another app, it will show here.`}
            />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DropdownSelect
                  label={'Integration Provider'}
                  variant="single"
                  value={sourceProviderOption}
                  options={sourceOptions}
                  onChange={handleSourceChange}
                  disabled={!isEditable}
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label={sourceProviderOption?.value === 'workglue' ? 'Job ID' : 'Integration Source ID'}
                  value={details.source?.id ?? ''}
                  onChange={handleSourceIdChange}
                  fullWidth={true}
                  disabled={!editable}
                />
              </Grid>
              {sourceProviderOption?.value === 'workglue' && (
                <Grid item xs={12}>
                  <InputField
                    label={`Account Id`}
                    value={details.source?.accountId ?? ''}
                    onChange={handleAccountIdChange}
                    fullWidth={true}
                    disabled={!editable}
                  />
                </Grid>
              )}
              {/* {quote.source?.provider?.startsWith('salesforce') && (
                <Grid item xs={12}>
                  <h4>SalesForce</h4>
                  {!!quote.source.opportunityId && (
                    <p>
                      <a
                        // href={`https://go.servicetitan.com/#/customer/` + salesforceSource.opportunityId}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Opportunity ({quote.source.opportunityId})
                      </a>
                    </p>
                  )}
                </Grid>
              )} */}
              {!!serviceTitanSource && (
                <Grid item xs={12}>
                  <h4>ServiceTitan</h4>
                  {!!serviceTitanSource.customerId && (
                    <p>
                      <a
                        href={`https://go.servicetitan.com/#/customer/` + serviceTitanSource.customerId}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Customer ({serviceTitanSource.customerId})
                      </a>
                    </p>
                  )}
                  {!!serviceTitanSource.estimateId && (
                    <p>
                      <a
                        href={`https://go.servicetitan.com/#/estimate/` + serviceTitanSource.estimateId}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Estimate ({serviceTitanSource.estimateId})
                      </a>
                    </p>
                  )}
                  {!!serviceTitanSource.invoiceId && (
                    <p>
                      <a
                        href={`https://go.servicetitan.com/#/EditInvoice/` + serviceTitanSource.invoiceId}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Invoice ({serviceTitanSource.invoiceId})
                      </a>
                    </p>
                  )}
                  {!!serviceTitanSource.dealId && (
                    <p>
                      <a
                        href={`https://go.servicetitan.com/#/Job/Index/` + serviceTitanSource.dealId}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Job ({serviceTitanSource.dealId})
                      </a>
                    </p>
                  )}
                  {!!serviceTitanSource.projectId && (
                    <p>
                      <a
                        href={`https://go.servicetitan.com/#/project/` + serviceTitanSource.projectId}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Project ({serviceTitanSource.projectId})
                      </a>
                    </p>
                  )}
                </Grid>
              )}
            </Grid>
          </FormSection>
        )}
      </>
    )
  }

  return (
    <div className={classes.root} onClick={onClick}>
      <EditableToggle
        position={editToggleAnchor}
        editable={isEditable}
        disabled={trialExpired || !permissionToEdit}
        onToggle={() => {
          setIsEditing(true)
          onToggleEdit(true)
        }}
        onClose={() => setEditToggleAnchor(null)}
      >
        {renderAdvancedFields()}
        {advancedOptionsFeature.enabled && renderQuoteOptions(advancedOptionsFeature)}
        {showDebugInfo && (
          <>
            <FormSection classes={{ root: classes.formSection }}>
              <FormSectionTitle
                title={
                  <Tooltip
                    content={
                      <Typography>
                        This will prevent <strong>any non-superadmin user</strong> from editing the document.
                      </Typography>
                    }
                    icon={<WarningIcon className={classes.warningIcon} />}
                  >
                    <>Calculation Lock</>
                  </Tooltip>
                }
                variant="h4"
              />

              <Grid container>
                <Grid item xs={12}>
                  <FormControlLabel
                    key={'calculationLock'}
                    control={<Checkbox checked={quote.calculationLock} onChange={handleCalculationLock} />}
                    label={'Lock Calculation'}
                    classes={{ label: classes.checkboxLabel }}
                  />
                </Grid>
              </Grid>
              {(!quote?.version || quote.version < CURRENT_QUOTE_VERSION) && (
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="body1">Outdated Quote Version - {quote?.version ?? 'None'} </Typography>
                    <Button onClick={() => handleVersionUpdate()}>Update Version</Button>
                  </Grid>
                </Grid>
              )}
            </FormSection>
            <FormSection classes={{ root: classes.formSection }}>
              <FormSectionTitle
                title={
                  <Tooltip
                    content={
                      <Typography>
                        This will paginate the work order into smaller sections to ease rendering strain on large
                        quotes.
                      </Typography>
                    }
                    icon={<WarningIcon className={classes.warningIcon} />}
                  >
                    <>Work Order Paginate</>
                  </Tooltip>
                }
                variant="h4"
              />

              <Grid container>
                <Grid item xs={12}>
                  <InputField label={'Page Size'} type="number" value={paginate} onChange={handlePaginateChange} />
                </Grid>
              </Grid>
            </FormSection>
          </>
        )}
        {showDebugInfo && (
          <FormSection classes={{ root: classes.formSection }}>
            <FormSectionTitle
              classes={{
                root: classes.debugInfoTitle
              }}
              title={`Debug Info`}
              variant="h4"
              rightContent={
                <Button onClick={handleIssueReport} size="small" variant="text">
                  Report Bug
                </Button>
              }
            />
            <Grid container>
              <Grid item xs={12}>
                <CopyToClipboard
                  text={debugInfo}
                  onCopy={() => enqueueSnackbar(`Debug info copied to clipboard.`, { variant: 'success' })}
                >
                  <pre>{debugInfo}</pre>
                </CopyToClipboard>
              </Grid>
            </Grid>
          </FormSection>
        )}
      </EditableToggle>
    </div>
  )
}

export default withStyles(styles)(withClientOptions(withQuoteContext(QuoteAdvancedForm)))
